import type { Document, DocumentCategory } from '@/lib/schemas'
import {
  BID_DOCUMENT_CATEGORY_LABELS,
  DocumentCategory as DocumentCategoryEnum,
  isAddendumDocument,
} from '@/lib/schemas'

/**
 * Grouping rules for the bid Documents tab (pack map PR3, spec 25 §4.2).
 *
 * The backend decides each row's group (`effectiveCategory`) and the frontend
 * only orders and shows it. When the list came from an older backend (or any
 * endpoint that omits the enrichment), the row falls back to today's rule:
 * an active addendum sits under Addenda, everything else under its stored type.
 */

/** The tender groups, in the order a bid manager reads a pack. `other` is always last. */
const LEADING_GROUPS: readonly DocumentCategory[] = [
  'rfp_tender',
  'addenda',
  'site_technical',
  'pricing',
  'policy_certification',
]

/**
 * Where "Move to" can send a document. Addenda is deliberately absent — a
 * document becomes an addendum through "Treat as addendum", which also sets up
 * reconciliation; a plain category move would skip that.
 */
export const BID_MOVE_TO_CATEGORIES: readonly DocumentCategory[] = [
  'rfp_tender',
  'site_technical',
  'pricing',
  'policy_certification',
  'other',
]

/**
 * A category value this frontend has no label for, made readable:
 * `future_category` → "Future category". The list is not Zod-parsed, so a
 * category the backend adds before the frontend knows it arrives as a raw
 * string — it must still render, not crash (an older frontend did).
 */
export function humaniseCategory(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim()
  if (!words) return 'Other'
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/** Bid-surface label for a category, falling back safely for unknown values. */
export function bidDocumentCategoryLabel(category: string): string {
  return Object.prototype.hasOwnProperty.call(BID_DOCUMENT_CATEGORY_LABELS, category)
    ? BID_DOCUMENT_CATEGORY_LABELS[category as DocumentCategory]
    : humaniseCategory(category)
}

/** The group a bid document is shown under. */
export function bidDocumentCategory(
  doc: Pick<Document, 'type' | 'metadata' | 'effectiveCategory'>
): DocumentCategory {
  if (doc.effectiveCategory) return doc.effectiveCategory
  return isAddendumDocument(doc) ? 'addenda' : doc.type
}

/**
 * Sort rank of a group: the five tender groups first, then any library /
 * reference category present on the bid (in enum order), then any category
 * this frontend does not know yet, then Other.
 */
function groupRank(category: DocumentCategory): number {
  if (category === 'other') return Number.MAX_SAFE_INTEGER
  const leading = LEADING_GROUPS.indexOf(category)
  if (leading !== -1) return leading
  const known = DocumentCategoryEnum.options.indexOf(category)
  if (known !== -1) return LEADING_GROUPS.length + known
  return LEADING_GROUPS.length + DocumentCategoryEnum.options.length
}

/**
 * Group order. Two unknown categories share a rank, so they fall back to
 * their labels — a stable, readable order rather than arrival order.
 */
export function compareBidDocumentCategories(a: DocumentCategory, b: DocumentCategory): number {
  return (
    groupRank(a) - groupRank(b) ||
    bidDocumentCategoryLabel(a).localeCompare(bidDocumentCategoryLabel(b))
  )
}

export interface BidDocumentGroup {
  category: DocumentCategory
  documents: Document[]
}

/**
 * Split documents into groups in canonical order. Empty groups are omitted,
 * and each group keeps the incoming order (the list's sort).
 */
export function groupBidDocuments(documents: Document[]): BidDocumentGroup[] {
  const byCategory = new Map<DocumentCategory, Document[]>()
  for (const doc of documents) {
    const category = bidDocumentCategory(doc)
    const bucket = byCategory.get(category)
    if (bucket) bucket.push(doc)
    else byCategory.set(category, [doc])
  }
  return Array.from(byCategory.entries())
    .sort(([a], [b]) => compareBidDocumentCategories(a, b))
    .map(([category, docs]) => ({ category, documents: docs }))
}

/**
 * Copy for the analysis-membership exception, or null when there is nothing
 * to say. Shown only on readable rows: a file still processing, failed or with
 * no searchable text already carries its own status, and "not in the analysis"
 * would only restate it.
 */
export function analysisMembershipNote(
  doc: Pick<Document, 'readability' | 'analysisMembership'>
): string | null {
  if (doc.readability !== 'readable') return null
  if (doc.analysisMembership === 'not_included') return 'Not in latest analysis'
  if (doc.analysisMembership === 'added_after') return 'Not in latest analysis (added after it ran)'
  return null
}
